import React, { useState, useEffect, useCallback } from "react";
import Helmet from "react-helmet";
import { useTheme } from "styled-components";
import { keyBy } from "lodash";
import { pluralize } from "@wrstudios/utils";
import { Markers } from "@wrstudios/components";
import { getPinsWithinBounds, getListingsById } from "../../../api/forge";
import { transformForgePin, transformForgeListing } from "../../../utils/forge";
import { useSession } from "../../../Session";
import Map from "../../common/Map";
import { setPreferredCompsSection } from "../utils/storage";
import { useLive } from "../LiveProvider";
import LiveHeader from "../common/LiveHeader";
import LiveListingPreview, {
  useListingPreviewResizer
} from "../listing/LiveListingPreview";
import { Container, Loading, LoadingPins } from "./styled/live-map";

function LiveMap() {
  const { mls, currentUser } = useSession();
  const {
    report,
    agent,
    subjectProperty,
    visibleListings,
    listingsWithForgeListings
  } = useLive();
  const { headerRef, mobileHeaderRef, previewRef, headerDetails } =
    useListingPreviewResizer();
  const [pins, setPins] = useState([]);
  const [hidePins, setHidePins] = useState(true);
  const [loadingPins, setLoadingPins] = useState(false);
  const [previewListingId, setPreviewListingId] = useState(null);
  const [previousMarker, setPreviousMarker] = useState(null);

  const forgePins = useForgePins({
    pins,
    previewListingId,
    setPreviewListingId,
    previousMarker,
    setPreviousMarker
  });
  const forgeClusters = useForgeClusters({
    pins,
    setPreviousMarker,
    setPreviewListingId
  });
  const listingMarkers = useListingMarkers({
    previewListingId,
    setPreviewListingId
  });
  const markers = [
    ...[...forgePins, ...forgeClusters].filter(() => !hidePins),
    ...listingMarkers
  ];

  const handleBoundsChange = async ({ zoom, mapBounds }) => {
    if (currentUser.guid !== agent.guid) return;
    if (zoom >= 15 && mls.code) {
      setHidePins(false);
      setLoadingPins(true);
      const pins = await getPinsWithinBounds({
        mlsCode: mls.code,
        mlsStatuses: mls.statuses,
        bounds: mapBounds,
        monthsBack: report.monthsBack,
        propType: report.propType,
        propSubType: report.propSubType
      });

      // merge pins instead of overwriting
      setPins((currentPins) => [
        ...currentPins,
        ...pins
          .map(transformForgePin)
          // merge pins with their store counterparts
          .map((pin) => {
            return {
              ...pin,
              listings: pin.listings.map((listing) => {
                const storeListing = listingsWithForgeListings.find(
                  (l) => l.id === listing.id
                );
                if (storeListing) {
                  listing.recordId = storeListing.recordId;
                }
                return listing;
              })
            };
          })
          .filter(
            (transformedPin) =>
              !currentPins.find(
                (currentPin) =>
                  currentPin.lat === transformedPin.lat &&
                  currentPin.lon === transformedPin.lon
              )
          )
      ]);
      setLoadingPins(false);
    } else {
      setHidePins(true);
    }
  };

  useEffect(() => {
    setPreferredCompsSection("map");
  }, []);

  useEffect(() => {
    const listing = visibleListings.find(
      (listing) => listing.id === previewListingId
    );
    if (listing && previewListingId !== listing.recordId) {
      setPreviewListingId(listing.recordId);
      setPins((pins) =>
        pins.map((pin) => {
          return {
            ...pin,
            listings: pin.listings.map((l) => {
              if (l.id === previewListingId) {
                l.recordId = listing.recordId;
              }
              return l;
            })
          };
        })
      );
    }
  }, [previewListingId, visibleListings, setPreviewListingId]);

  return (
    <>
      <Helmet>
        <title>Map - Comps</title>
      </Helmet>
      <LiveHeader headerRef={headerRef} mobileHeaderRef={mobileHeaderRef}>
        {pluralize("Comparable Homes", visibleListings.length, true)}
      </LiveHeader>
      <Container>
        {loadingPins && (
          <LoadingPins>
            <Loading />
          </LoadingPins>
        )}
        <Map
          markers={markers}
          boundMarkers={listingMarkers}
          spLat={subjectProperty.coords.lat}
          spLon={subjectProperty.coords.lon}
          onBoundsUpdate={handleBoundsChange}
          onLoaded={handleBoundsChange}
          isAutoZoomDisabled
          isScrollZoomEnabled
          isInitialZoomDisabled
          isZoomToSeeMoreEnabled
        />
        {previewListingId && (
          <LiveListingPreview
            ref={previewRef}
            style={{ top: headerDetails.bottom }}
            listingRecordId={previewListingId}
            initialTab="tour"
            showInModal={true}
            onClose={() => setPreviewListingId(null)}
          />
        )}
      </Container>
    </>
  );
}

function Cluster({ pin, listings, setPreviewListingId }) {
  const theme = useTheme();
  const { getListingsFromPin, addForgeListings } = useForge();
  const [request, setRequest] = useState({ loading: false, success: false });

  useEffect(() => {
    if (request.loading || request.success) return;
    setRequest({ loading: true, success: false });
    getListingsFromPin(pin).then((listings) => {
      addForgeListings(listings);
      setRequest({ loading: false, success: true });
    });
  }, [request, pin, addForgeListings]);

  return (
    <Markers.ClusterPopup>
      {!listings.length && <Loading />}
      {listings.map((listing) => (
        <Markers.ClusterListing
          key={listing.id}
          id={listing.id}
          street={listing.street}
          city={listing.city}
          state={listing.state}
          zip={listing.zip}
          price={listing.price}
          photo={listing.photos[0]}
          status={listing.status}
          statusColor={theme.getStatusColorSpectrum(listing.mappedStatus)[700]}
          statusBackgroundColor={
            theme.getStatusColorSpectrum(listing.mappedStatus)[100]
          }
          onClick={(e) => {
            e.stopPropagation();
            setPreviewListingId(listing.id);
          }}
        />
      ))}
    </Markers.ClusterPopup>
  );
}

function useForge() {
  const { mls } = useSession();
  const { setForgeListings } = useLive();

  const getListingsFromPin = useCallback(async (pin) => {
    const listingIds = pin.listings.map((listing) => listing.id);
    const listings = await getListingsById({
      mlsCode: mls.code,
      mlsStatuses: mls.statuses,
      listingIds
    });
    return listings.map(transformForgeListing);
  }, []);

  const addForgeListings = useCallback((listings) => {
    setForgeListings((forgeListings) => {
      const forgeListingsById = keyBy(forgeListings, "id");
      const listingsById = keyBy(listings, "id");
      return Object.values({ ...forgeListingsById, ...listingsById });
    });
  }, []);

  return {
    getListingsFromPin,
    addForgeListings
  };
}

function useForgePins({
  pins,
  previewListingId,
  setPreviewListingId,
  previousMarker,
  setPreviousMarker
}) {
  const { visibleListings } = useLive();
  const { getListingsFromPin, addForgeListings } = useForge();
  const listingIds = visibleListings.map((listing) => listing.id);
  const filteredPins = pins
    .filter((pin) => !pin.isCluster)
    .filter((pin) => {
      return !pin.listings.find((listing) => listingIds.includes(listing.id));
    });

  return filteredPins.map((pin) => {
    const [firstListing] = pin.listings;
    const isSelected =
      firstListing.id === previewListingId ||
      firstListing.recordId === previewListingId;

    return {
      id: pin.markerId,
      lat: pin.lat,
      lon: pin.lon,
      zIndex: isSelected ? 2 : null,
      component: (
        <Markers.PinMarker
          isSelected={isSelected}
          onClick={async ({ event, map }) => {
            if (previousMarker === "pin") {
              event.stopPropagation();
            }
            setPreviousMarker("pin");
            map.easeTo({ center: [pin.lon, pin.lat] });
            const listings = await getListingsFromPin(pin);
            addForgeListings(listings);
            setPreviewListingId(firstListing.recordId);
          }}
          color={firstListing.statusColor}
        />
      )
    };
  });
}

function useForgeClusters({ pins, setPreviousMarker, setPreviewListingId }) {
  const { forgeListings } = useLive();
  const filteredPins = pins.filter((pin) => pin.isCluster);

  return filteredPins.map((pin) => {
    const [firstListing] = pin.listings;
    const listingIds = pin.listings.map((listing) => listing.id);
    const forgeListingsById = keyBy(forgeListings, "id");
    const listings = listingIds
      .map((listingId) => forgeListingsById[listingId])
      .filter(Boolean);

    return {
      id: pin.markerId,
      lat: Number(pin.lat),
      lon: Number(pin.lon),
      component: (
        <Markers.ClusterMarker
          color={firstListing.statusColor}
          onClick={({ map }) => {
            map.easeTo({ center: [pin.lon, pin.lat] });
            setPreviewListingId(null);
            setPreviousMarker("cluster");
          }}>
          {pin.listings.length}
        </Markers.ClusterMarker>
      ),
      popupComponent: (
        <Cluster
          offsetY={-40}
          pin={pin}
          listings={listings}
          setPreviewListingId={setPreviewListingId}
        />
      )
    };
  });
}

function useListingMarkers({ previewListingId, setPreviewListingId }) {
  const { visibleListings } = useLive();

  return visibleListings.map((listing) => {
    const isSelected =
      listing.id === previewListingId || listing.recordId === previewListingId;
    return {
      id: listing.recordId,
      lat: listing.coords.lat,
      lon: listing.coords.lon,
      zIndex: isSelected ? 2 : null,
      component: (
        <Markers.ListingMarker
          isSelected={isSelected}
          price={listing.price}
          onClick={({ event, map }) => {
            event.stopPropagation();
            map.easeTo({ center: [listing.coords.lon, listing.coords.lat] });
            setPreviewListingId(listing.recordId);
          }}
          color={listing.statusColor}
        />
      )
    };
  });
}

export default LiveMap;
